import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../ui/dialog";

const ASSET_BASE = "https://customer-assets.emergentagent.com/job_stac-detail-book/artifacts";

// Photos the customer has explicitly chosen for the gallery slideshow.
const EXTERIOR = [
  "/gallery/ext_merc_glc_front.png",
  "/gallery/ext_jaecoo_front.png",
  "/gallery/ext_bmw_estate_side.png",
  "/gallery/exterior_vw_golf_clean.png",
  "/gallery/ext_mercedes_side.png",
  "/gallery/ext_bmw_front.png",
  "/gallery/ext_mercedes_front.png",
  `${ASSET_BASE}/hzqf1udg_IMG_0687.jpeg`,
];

const INTERIOR = [
  `${ASSET_BASE}/34i4he03_IMG_0796.webp`,
  `${ASSET_BASE}/misy79gc_IMG_0797.webp`,
  `${ASSET_BASE}/p1zaguko_IMG_0793.webp`,
  `${ASSET_BASE}/k1bj6l7b_IMG_0694.jpeg`,
  `${ASSET_BASE}/ybwj8cip_IMG_0695.jpeg`,
  `${ASSET_BASE}/p3u48p8m_IMG_0692.jpeg`,
  `${ASSET_BASE}/tskt3i6s_IMG_0685.jpeg`,
];

function CategoryCard({ label, cover, count, testid }) {
  return (
    <div
      data-testid={`gallery-${testid}-card`}
      className="group relative block w-full overflow-hidden bg-[#111] aspect-[4/5] md:aspect-[3/4] cursor-pointer"
      role="button"
      tabIndex={0}
    >
      <img
        src={cover}
        alt={`${label} cover`}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1400ms] ease-out group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10" />
      <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 text-left">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2">
          {String(count).padStart(2, "0")} shots
        </div>
        <div className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mb-3">
          {label}
        </div>
        <div className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-white/80 group-hover:text-white transition-colors">
          <span>View slideshow</span>
          <span className="block w-8 h-px bg-white/60 group-hover:w-12 transition-all duration-500" />
        </div>
      </div>
    </div>
  );
}

const SWIPE_THRESHOLD_PX = 50;

function Slideshow({ images, label, testid, isOpen }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);

  // Reset to first slide whenever the dialog opens.
  useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen]);

  // Keyboard navigation while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, next, prev]);

  // Auto-advance intentionally disabled — users navigate manually (swipe/arrows/dots).

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > SWIPE_THRESHOLD_PX) {
      if (delta < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <div
      data-testid={`gallery-${testid}-slideshow`}
      className="relative w-full h-[90vh] max-h-[90vh] flex flex-col bg-black text-white"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-white/10">
        <div>
          <div className="overline mb-1">— Our Work</div>
          <div className="font-display text-xl md:text-2xl font-light tracking-tight">{label}</div>
        </div>
        <div
          data-testid={`gallery-${testid}-counter`}
          className="text-xs tracking-[0.3em] uppercase text-white/60 tabular-nums"
        >
          {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
        </div>
      </div>

      {/* Stage */}
      <div
        className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden bg-[#070707] select-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          key={images[index]}
          src={images[index]}
          alt={`${label} ${index + 1}`}
          draggable={false}
          className="max-w-full max-h-full object-contain animate-in fade-in duration-500"
        />
      </div>

      {/* Dot indicators */}
      <div className="flex items-center justify-center gap-2 py-4 border-t border-white/10">
        {images.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`gallery-${testid}-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "bg-white w-8" : "bg-white/30 hover:bg-white/60 w-3"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Gallery() {
  const [openExterior, setOpenExterior] = useState(false);
  const [openInterior, setOpenInterior] = useState(false);

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="px-6 md:px-12 lg:px-20 py-28 md:py-40 bg-[#070707]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <div className="overline mb-5">— Our Work</div>
            <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
              The proof is on the paintwork.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-white/60 leading-relaxed text-sm">
              A handful of recent details from real customers. Tap a category to flick through the slideshow.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
          <Dialog open={openExterior} onOpenChange={setOpenExterior}>
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={() => setOpenExterior(true)}
                className="text-left focus:outline-none focus:ring-2 focus:ring-white/40"
                data-testid="gallery-exterior-trigger"
              >
                <CategoryCard
                  label="Exterior"
                  cover={EXTERIOR[0]}
                  count={EXTERIOR.length}
                  testid="exterior"
                />
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid="gallery-exterior-dialog"
              className="max-w-6xl w-[96vw] h-[90vh] p-0 gap-0 bg-black border border-white/10 rounded-none sm:rounded-md overflow-hidden text-white"
            >
              <DialogTitle className="sr-only">Exterior work slideshow</DialogTitle>
              <Slideshow
                images={EXTERIOR}
                label="Exterior"
                testid="exterior"
                isOpen={openExterior}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={openInterior} onOpenChange={setOpenInterior}>
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={() => setOpenInterior(true)}
                className="text-left focus:outline-none focus:ring-2 focus:ring-white/40"
                data-testid="gallery-interior-trigger"
              >
                <CategoryCard
                  label="Interior"
                  cover={INTERIOR[0]}
                  count={INTERIOR.length}
                  testid="interior"
                />
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid="gallery-interior-dialog"
              className="max-w-6xl w-[96vw] h-[90vh] p-0 gap-0 bg-black border border-white/10 rounded-none sm:rounded-md overflow-hidden text-white"
            >
              <DialogTitle className="sr-only">Interior work slideshow</DialogTitle>
              <Slideshow
                images={INTERIOR}
                label="Interior"
                testid="interior"
                isOpen={openInterior}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}
